<template>
  <div class="container">
    <FullCalendar
        :events="getEvents()"
        :plugins="calendarPlugins"
        default-view="dayGridMonth"
        locale="nl"
        :firstDay="1"
        :header="{left: 'title', center: isLocked ? (isShowingLockedVersion ? 'showActual' : 'showLocked') : '', right: getButtons()}"
        :weekends="true"
        ref="fullCalendar"
        :default-date='initialMonth'
        :showNonCurrentDates="false"
        eventTextColor="white"
        :height="500"
        :contentHeight="500"
        :customButtons="customButtons"/>

    <div class="row border">
      <div class="col-lg-12">
        <p>
          Feestdagen hebben een <span class="text-red">rode</span> achtergrond
          <br>
          Dagen waarop al een shift staat hebben een <span class="bg-lightgreen">groene</span>
          achtergrond
        </p>
        <p>Ingevuld:
           <span :class="getClassForWorkedHours()">
               {{ countedHours | convertDotToCommaInNumber }}
           </span>
          /{{ neededHours | convertDotToCommaInNumber }} uren
        </p>
        <p>Opgebouwde Recuperatie: {{ recupPreviousHours | convertDotToCommaInNumber }} uren / {{ recupPreviousInDays | convertDotToCommaInNumber  }} dagen
          <br>
          Opgenomen Recuperatie: {{ recupTakenHours | convertDotToCommaInNumber }} uren / {{ recupTakenInDays | convertDotToCommaInNumber  }} dagen
          <br>
          Resterende vakantie: {{ remainingVacationDays | convertDotToCommaInNumber }} dagen
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import {mapActions, mapGetters, mapMutations} from "vuex";
import {toYearAndMonth} from "@/util/DateUtil";

export default {
  name: "AdminContractDashboard",
  components: {
    FullCalendar
  },
  props: ['contract'],
  data() {
    return {
      calendarPlugins: [dayGridPlugin, timeGridPlugin, listPlugin],
      fullCalendar: null,
      initialMonth: toYearAndMonth(this.currentlySelectedMonth),
      customButtons: {
        next: {
          click: () => this.clickNext()
        },
        prev: {
          click: () => this.clickPrev()
        },
        showLocked: {
          text: "Toon vastgezette planning",
          click: () => this.clickShowLocked()
        },
        showActual: {
          text: "Toon actuele planning",
          click: () => this.clickShowActual()
        },
      }
    };
  },
  computed: {
    ...mapGetters({
      plannedShifts: "timesheet/plannedShifts",
      holidays: "timesheet/holidays",
      occupiedDays: "timesheet/occupiedDays",
      absenceDays: "timesheet/absenceDays",
      countedHours: "timesheet/countedHours",
      neededHours: "timesheet/neededHours",
      workedEnough: "timesheet/workedEnough",
      recupPreviousHours: "timesheet/recupPreviousHours",
      recupTakenHours: "timesheet/recupTakenHours",
      recupPreviousInDays: "timesheet/recupPreviousInDays",
      recupTakenInDays: "timesheet/recupTakenInDays",
      remainingVacationDays: "timesheet/remainingVacationDays",
      isLocked: "timesheet/isLocked",
      isShowingLockedVersion: "administrationTimeSheet/isShowingLockedVersion",
      previousMonthAvailable: "administrationTimeSheet/previousMonthAvailable",
      nextMonthAvailable: "administrationTimeSheet/nextMonthAvailable",
      currentlySelectedMonth: "time/currentlySelectedMonth"
    }),
  },
  created() {
    this.resetStateBeforeRenderingFullCalendar();
    this.fetchAbsenceDataForSelectedTimeSheet();
    this.fetchPlannedDataForSelectedTimeSheet();
  },
  mounted() {
    this.fullCalendar = this.$refs.fullCalendar.getApi();
    this.fullCalendar.gotoDate(toYearAndMonth(this.currentlySelectedMonth));
  },
  methods: {
    ...mapActions("administrationTimeSheet", [
      'fetchPlannedDataForSelectedTimeSheet',
      'fetchAbsenceDataForSelectedTimeSheet',
      'fetchLockedDataForSelectedTimeSheet',
      'fetchLockedAbsenceDataForSelectedTimeSheet'
    ]),
    ...mapMutations({
      setIsShowingLockedVersion: "administrationTimeSheet/setIsShowingLockedVersion",
      resetStateBeforeRenderingFullCalendar: "timesheet/resetStateBeforeRenderingFullCalendar",
      addMonth: "time/addMonth",
      subtractMonth: "time/subtractMonth"
    }),
    getClassForWorkedHours() {
      return this.workedEnough ? 'text-green' : 'text-red';
    },
    clickNext() {
      this.addMonth();
      this.fullCalendar.next();
      this.setIsShowingLockedVersion(false);
      this.fetchDataForShowedMonth();
    },
    clickPrev() {
      this.subtractMonth();
      this.fullCalendar.prev();
      this.setIsShowingLockedVersion(false);
      this.fetchDataForShowedMonth();
    },
    async clickShowLocked() {
      await Promise.all([this.fetchLockedDataForSelectedTimeSheet(), this.fetchLockedAbsenceDataForSelectedTimeSheet()])
          .then(() => this.setIsShowingLockedVersion(true));
    },
    async clickShowActual() {
      await Promise.all([this.fetchPlannedDataForSelectedTimeSheet(), this.fetchAbsenceDataForSelectedTimeSheet()])
          .then(() => this.setIsShowingLockedVersion(false));
    },
    fetchDataForShowedMonth() {
      this.fetchAbsenceDataForSelectedTimeSheet();
      this.fetchPlannedDataForSelectedTimeSheet();
    },
    getEvents() {
      return [
        ...this.plannedShifts,
        ...this.holidays,
        ...this.occupiedDays,
        ...this.absenceDays,
        ...this.getWeekendDays()
      ];
    },
    getWeekendDays() {
      return [{
        daysOfWeek: [0, 6],
        rendering: 'background',
        color: '#D0D0D0',
        allDay: true
      }];
    },
    getButtons(){
      const prev = this.previousMonthAvailable ? 'prev ' : '';
      const next = this.nextMonthAvailable ? 'next' : '';
      return prev + next;
    }
  }
};
</script>

<style scoped>
@import '~@fullcalendar/core/main.css';
@import '~@fullcalendar/daygrid/main.css';

.text-red {
  color: red;
}

/*noinspection CssUnusedSymbol*/
.text-green {
  color: green;
}

.bg-lightgreen {
  background-color: lightgreen;
}
</style>
